//
// 在庫管理システム[375]の全在庫データのエクセル入出力
//
//  ExcelInOutModal.js
//
// 在庫一覧_YYYY-MM-DD.xlsx のファイル名のみ限定して対応。
//

import React, { useRef, useState } from "react";
import { useEscapeClose } from "../hooks/useEscapeClose";

export default function ExcelInOutModal({ isOpen, onClose, fetchInventoryFromServer }) {
  const fileInputRef = useRef(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [stage, setStage] = useState("idle"); // idle | confirm | processing | done | error
  const [message, setMessage] = useState("");
  const [processing, setProcessing] = useState(false);

  // escキーで閉じる
  useEscapeClose(() => {
    if (!processing) {
      onClose();
    }
  });

//

  if (!isOpen) return null;

  // 書き出し
  const handleExport = () => {
    window.location.href = "https://newells4610.stars.ne.jp/api/export_excel_all.php";
  };

  // 書き戻し（ファイル選択トリガー）
  const handleImportClick = () => {
    if (processing) return;
    fileInputRef.current?.click();
  };

  const onFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!/^在庫一覧_\d{4}-\d{2}-\d{2}\.xlsx$/.test(file.name)) {
      alert("在庫一覧_YYYY-MM-DD.xlsx の形式のみアップロードできます。");
      e.target.value = "";
      return;
    }

    setSelectedFile(file);
    setStage("confirm");
  };

  const handleConfirmCancel = () => {
    setSelectedFile(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
    setStage("idle");
  };

  const handleConfirmOk = async () => {
    if (!selectedFile) return;
    setProcessing(true);
    setStage("processing");
    setMessage("");

    const formData = new FormData();
    formData.append("excelFile", selectedFile);

    try {
      const res = await fetch("https://newells4610.stars.ne.jp/api/import_excel_all.php", {
        method: "POST",
        body: formData,
      });
      const data = await res.json();
      if (data.success) {
        setMessage(data.message || "全データの書き戻しが完了しました");
        setStage("done");
      } else {
        setMessage(data.message || "書き戻しに失敗しました");
        setStage("error");
      }
    } catch (err) {
      setMessage(err?.message || String(err));
      setStage("error");
    } finally {
      setProcessing(false);
    }
  };

  const handleFinalClose = () => {
    setSelectedFile(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
    setStage("idle");
    onClose();
    
    // モーダルを閉じたら必ずデータ更新して最新化
    if (typeof fetchInventoryFromServer === "function") {
      fetchInventoryFromServer();
    }
  };

// ------------------------------------------------------------------------------

  return (
    <div className="modal-overlay">
      <div className="modal-box">
        <h2 className="text-xl font-bold mb-4">在庫一覧 入出力</h2>

        {/* 書き出し */}
        <div className="mb-6">
          <button
            onClick={handleExport}
            className="admin-button"
            style={{
              backgroundColor: "#aff",
              padding: "8px 16px",   // ← 上下8px、左右16px
              fontSize: "16px",      // ← 文字サイズ
              borderRadius: 5,
              cursor: "pointer"
            }}
            disabled={processing}
          >
            ◀◀書き出し▶▶　　KDinventoryData.json　➠　在庫一覧Excelファイル　
          </button>
          <p>
            KD在庫管理システムの全在庫データをエクセル出力します。
          </p>
        </div>

        <p></p>

        {/* 書き戻し */}
        <div>
          <button
            onClick={handleImportClick}
            className="admin-button"
            style={{
              backgroundColor: "#faa",
              padding: "8px 16px",   // ← 上下8px、左右16px
              fontSize: "16px",      // ← 文字サイズ
              borderRadius: 5,
              cursor: "pointer"
            }}
            disabled={processing}
          >
            ▶▶書き戻し◀◀　　在庫一覧Excelファイル　➠　KDinventoryData.json　
          </button>
          <p>
            KD在庫管理システムで出力したエクセルの形式から本体へ<br />
            すべて [[上書き]] で書き戻します。【要注意】
          </p>
          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx"
            style={{ display: "none" }}
            onChange={onFileChange}
          />
        </div>

        {/* 確認モーダル */}
        {stage === "confirm" && selectedFile && (
          <div className="modal-overlay">
            <div className="modal-box">
              <p>
                KD在庫管理[375] に  <strong>{selectedFile.name}</strong>  を 上書き で
                書き戻します。
              </p>
              <p>
                本当にこのファイルで書き戻していいですか？
              </p>
              <div className="modal-actions">
                <button
                  onClick={handleConfirmCancel}
                  className="admin-button"
                  style={{ backgroundColor: "#e5e7eb" }}
                >
                  キャンセル
                </button>
                <button
                  onClick={handleConfirmOk}
                  className="admin-button"
                  style={{ backgroundColor: "#dc2626", color: "white" }}
                >
                  OK
                </button>
              </div>
            </div>
          </div>
        )}

        {/* 処理中 -------------------------------------------------- */}
        {stage === "processing" && (
          <div className="mt-6 p-4 border rounded-lg bg-yellow-50">
            <p>書き戻しを実行中です。しばらくお待ちください…</p>
          </div>
        )}

        {/* 成功 ---------------------------------------------------- */}
        {stage === "done" && (
          <div
            className="modal-overlay"
            style={{ zIndex: 9999, backgroundColor: "rgba(0,0,0,0.5)" }}
          >
            <div
              className="modal-box"
              style={{ textAlign: "center", background: "white", padding: "20px" }}
            >
              <p style={{ marginBottom: "12px" }}>
                {message || "全データの書き戻しが完了しました"}
              </p>
              <button
                onClick={handleFinalClose}
                className="admin-button"
                style={{ backgroundColor: "#8af", padding: "0.6em 1.2em", border: "none", borderRadius: "0.5em" }}
              >
                OK
              </button>
            </div>
          </div>
        )}

        {/* エラー ---------------------------------------------------*/}
        {stage === "error" && (
          <div
            className="modal-overlay"
            style={{ zIndex: 9999, backgroundColor: "rgba(0,0,0,0.5)" }}
          >
            <div
              className="modal-box"
              style={{ textAlign: "center", background: "white", padding: "20px" }}
            >
              <p style={{ color: "red", marginBottom: "12px" }}>
                全データの書き戻しに失敗しました。
              </p>
              <p>{message}</p>
              <button
                onClick={handleFinalClose}
                className="admin-button"
                style={{ backgroundColor: "#f88", padding: "0.6em 1.2em", border: "none", borderRadius: "0.5em"  }}
              >
                OK
              </button>
            </div>
          </div>
        )}

        {/* 閉じる */}
        <div className="modal-actions mt-6">
          <button
            onClick={() => {
              if (!processing) handleFinalClose();
            }}
            className="admin-button"
            style={{ backgroundColor: "#e5e7eb" }}
            disabled={processing}
          >
            閉じる
          </button>
        </div>
      </div>
    </div>
  );
}

